// The SDK directly, not our use-intercom hook: that module reaches the profile query,
// which imports this file, and the cycle breaks the analytics bundle.
import { trackEvent as trackIntercomEvent } from '@intercom/messenger-js-sdk'
import { getIntercomAppId, isDev } from 'v2/configs/constants'
import { identifyHubspotContact } from 'v2/utils/hubspot'
import { isInternalEmail } from 'v2/utils/internal-user'
import { logger } from 'v2/utils/logger'

// Canonical event names. Using an enum prevents typos like `user_logged_ni`
// from silently shipping. Convention: snake_case, past tense for completed actions.
export enum AnalyticsEvents {
  // Signup / login funnel
  SIGNUP_INITIATED = 'signup_initiated',
  USER_SIGNED_UP = 'user_signed_up',
  USER_LOGGED_IN = 'user_logged_in',
  SIGNUP_BLOCKED = 'signup_blocked',
  PHONE_VERIFIED = 'phone_verified',
  ORG_CREATED = 'org_created',
  ORG_SWITCHED = 'org_switched',
  ACCOUNT_QUESTIONNAIRE_COMPLETED = 'account_questionnaire_completed',
  // Product
  BUDDY_MESSAGE_SENT = 'buddy_message_sent',
  BUDDY_TICKET_FAILED = 'buddy_ticket_failed',
  VIBE_FEEDBACK = 'vibe_feedback',
  PLAN_UPGRADED = 'plan_upgraded',
  CREDITS_RECHARGED = 'credits_recharged',
  CARD_ADDED = 'card_added',
  CARD_3DS_REDIRECT_ATTEMPTED = 'card_3ds_redirect_attempted',
  CARD_3DS_REDIRECT_BLOCKED = 'card_3ds_redirect_blocked',
  CARD_3DS_MANUAL_OPENED = 'card_3ds_manual_opened',
  NUMBER_SEARCH_OPENED = 'number_search_opened',
  COMPLIANCE_FLOW_STARTED = 'compliance_flow_started',
  COMPLIANCE_SUBMITTED = 'compliance_submitted',
  NUMBER_PURCHASE_SUCCEEDED = 'number_purchase_succeeded',
  VERIFY_APP_CREATED = 'verify_app_created',
  AGENT_CREATED = 'agent_created',
  // Billing / payments
  RECHARGE_INITIATED = 'recharge_initiated',
  PAYMENT_FAILED = 'payment_failed',
  ADD_CARD_STARTED = 'add_card_started',
  PLAN_CHANGE_VIEWED = 'plan_change_viewed',
  ORG_DEACTIVATED = 'org_deactivated',
  AUTO_RECHARGE_UPDATED = 'auto_recharge_updated',
  BANK_ACCOUNT_ADDED = 'bank_account_added',
  ACH_TOGGLED = 'ach_toggled',
  CARD_MANDATE_CREATED = 'card_mandate_created',
  BALANCE_ALERT_CONFIGURED = 'balance_alert_configured',
  PAYMENT_CALLBACK_RESOLVED = 'payment_callback_resolved',
}

export type AnalyticsProperties = Record<string, unknown>

// `transport: 'sendBeacon'` is the notable case — guarantees delivery during
// unload for events fired right before a reload/navigation.
export type CaptureOptions = {
  transport?: 'XHR' | 'sendBeacon' | 'fetch'
}

// Wrapper around `window.posthog?.capture` so ad-blockers/CSP/GTM-not-loaded
// degrade silently instead of crashing the app, and so we have one swap point
// if the provider changes.
/**
 * The subset of events Intercom also receives, for onboarding message triggers. Kept as an
 * allowlist rather than mirroring everything: these become trigger options in the Intercom
 * UI, and a list of every analytics event in the app would bury the few that matter.
 */
const INTERCOM_EVENTS = new Set<AnalyticsEvents>([
  // Deliberately NOT user_signed_up: it fires on the anonymous complete-signup page,
  // before the messenger has booted, so it could never arrive. Intercom's built-in
  // "Signed up" date rule already covers that trigger, driven by the created_at we send.
  AnalyticsEvents.ACCOUNT_QUESTIONNAIRE_COMPLETED,
  AnalyticsEvents.AGENT_CREATED,
  AnalyticsEvents.VERIFY_APP_CREATED,
  AnalyticsEvents.NUMBER_PURCHASE_SUCCEEDED,
  AnalyticsEvents.COMPLIANCE_SUBMITTED,
  AnalyticsEvents.PLAN_UPGRADED,
])

export const track = (
  event: AnalyticsEvents,
  properties?: AnalyticsProperties,
  options?: CaptureOptions,
) => {
  try {
    window.posthog?.capture(event, properties, options)
    // Guarded on window.Intercom: the SDK console-warns when the messenger has not loaded,
    // and most of these fire on pages a visitor reaches before it boots.
    if (INTERCOM_EVENTS.has(event) && getIntercomAppId() && window.Intercom) {
      trackIntercomEvent(event, properties)
    }
    if (isDev) logger.log(`[analytics] ${event}`, properties ?? {})
  } catch (error) {
    logger.error(`track failed for event "${event}"`, error)
  }
}

interface IdentifyUserProps {
  email?: string
  aomId?: string
  authId?: string
  name?: string
  roles?: string
  orgName?: string
  region?: string
  // Stable per-account identifier (org auth_id) used as the PostHog group key,
  // so account-level metadata attaches to the right account instead of a
  // non-unique org name.
  accountKey?: string
  // Fingerprint's id for the browser that submitted the signup; see utils/fingerprint.
  fingerprintVisitorId?: string
}

// identifyUser is called on every authenticated mount and again once the profile
// query resolves with the account key, so without a guard each page load re-sent
// the whole identity triple. PostHog dedupes identify() internally, but group()
// has no such guard and $set re-sends whenever the payload changes — which it does
// between those two calls, because the second one carries auth_id. One browser
// tab is one identity, so the last-sent payload is remembered per module.
let lastIdentifySignature = ''
let lastGroupSignature = ''

// Bun runs every test file in one process, so module state outlives a single
// file's cases — reset it in beforeEach rather than relying on fresh imports.
export const resetIdentityDedupe = () => {
  lastIdentifySignature = ''
  lastGroupSignature = ''
}

// Identifies the current user to PostHog by EMAIL — the person is the human, so
// a user's multiple accounts collapse to one person and web→app sessions stitch
// on the same key (email is also the HubSpot join key). `email` is required;
// without it PostHog has no stable distinct ID. `aomId` rides along as a person
// property only: it used to also be passed to posthog.alias(), but that fired on
// every call (no internal dedupe) and each one forces a person merge, so it cost
// ~6.4k events/day to redo a merge that has long since happened for every user.
// Empty/undefined fields are omitted so we never overwrite existing person
// properties with blanks (PostHog $set replaces values, including '').
export const identifyUser = (props: IdentifyUserProps) => {
  if (!props.email) return

  try {
    // Backstop for the one case the init-time gate in monitorToolsSetup cannot see: a
    // first login on a browser whose localStorage held no email when posthog.init ran.
    //
    // ACCEPTED GAP, stated because it will not be obvious later: whatever has already
    // flushed by now is a billed recording and stays one. This truncates a session, it
    // does not prevent it, so a staff member's very first login per browser leaves a
    // short replay behind. Raised in review on #3578; the alternative — init with
    // recording off and start it only once identify proves the user external — was
    // rejected because it drops replay for EVERY anonymous visitor (login, signup,
    // complete-signup, 2FA, and anyone who never logs in), ~2.3k sessions/week and
    // precisely the funnels we debug most often. The residual is also bounded in
    // practice: the project's replay URL blocklist pauses capture on `login` URLs and
    // `/home`, which is where a first login sits until useIdentifyAnalytics fires.
    //
    // Optional-called because the method is absent from the posthog stub in tests and
    // from a blocked/partial SDK load.
    if (isInternalEmail(props.email)) window.posthog?.stopSessionRecording?.()

    const setPayload: Record<string, string> = { email: props.email }
    if (props.name) setPayload.name = props.name
    if (props.roles) setPayload.roles = props.roles
    if (props.orgName) setPayload.orgName = props.orgName
    if (props.region) setPayload.region = props.region
    if (props.authId) setPayload.auth_id = props.authId
    if (props.aomId) setPayload.aom_uuid = props.aomId
    if (props.fingerprintVisitorId) setPayload.fingerprint_visitor_id = props.fingerprintVisitorId

    const identifySignature = `${props.email}|${JSON.stringify(setPayload)}`
    if (identifySignature !== lastIdentifySignature) {
      window.posthog?.identify(props.email, setPayload)
      lastIdentifySignature = identifySignature
    }

    // Bind to the account-level group so account metadata is scoped per account
    // (not per person) and never overwritten when the user switches accounts.
    // Requires the real account key: the old `accountKey || orgName` fallback fired
    // on the first render, before the profile query resolved, and org names are not
    // unique — so every login minted a duplicate org-name-keyed group (they had
    // grown to 68% of the project's company groups).
    if (props.accountKey) {
      const groupPayload: Record<string, string> = {}
      if (props.orgName) groupPayload.name = props.orgName
      if (props.region) groupPayload.data_region = props.region

      const groupSignature = `${props.accountKey}|${JSON.stringify(groupPayload)}`
      if (groupSignature !== lastGroupSignature) {
        window.posthog?.group('company', props.accountKey, groupPayload)
        lastGroupSignature = groupSignature
      }
    }

    identifyHubspotContact(props.email)
  } catch (error) {
    logger.error('identifyUser failed', error)
  }
}

interface AccountMetadataProps {
  accountKey?: string
  orgName?: string
  productBucket?: string
  cxUseCase?: string
  region?: string
}

// Sets account-level (PostHog group) metadata captured from the onboarding
// questionnaire. Stored on the account group — keyed by `accountKey` (org
// auth_id) — so a user's other accounts are never overwritten. Called once at
// submit; PostHog retains group properties, so we don't need the (un-persisted)
// answers again on later sessions. Requires the account key for the same reason
// identifyUser does: an org-name-keyed group is one nobody is a member of, since
// that is no longer how members are bound.
export const setAccountMetadata = (props: AccountMetadataProps) => {
  if (!props.accountKey) return

  try {
    const payload: Record<string, string> = {}
    if (props.orgName) payload.name = props.orgName
    if (props.productBucket) payload.product_bucket = props.productBucket
    if (props.cxUseCase) payload.cx_use_case = props.cxUseCase
    if (props.region) payload.data_region = props.region
    if (Object.keys(payload).length === 0) return

    window.posthog?.group('company', props.accountKey, payload)
  } catch (error) {
    logger.error('setAccountMetadata failed', error)
  }
}

// === Signup country bridge ===
// USER_SIGNED_UP fires from complete-signup.tsx, but the IP-derived country is
// detected on the earlier signup-v2.tsx page (via ipinfo.io). localStorage
// carries it across the verification-email round-trip even when the link
// opens in a new tab — sessionStorage would be empty there. We TTL the entry
// to 1 hour so abandoned signups don't pollute storage indefinitely.

const SIGNUP_COUNTRY_KEY = 'cx:signup:country_from_ip'
const SIGNUP_COUNTRY_TTL_MS = 60 * 60 * 1000 // 1 hour

interface SignupCountryEntry {
  country: string
  expiresAt: number
}

export const setSignupCountry = (country: string | null | undefined) => {
  if (!country) return
  try {
    const entry: SignupCountryEntry = {
      country,
      expiresAt: Date.now() + SIGNUP_COUNTRY_TTL_MS,
    }
    localStorage.setItem(SIGNUP_COUNTRY_KEY, JSON.stringify(entry))
  } catch {
    // ignored — analytics is best-effort
  }
}

// Read + clear in one shot — called by complete-signup.tsx right before
// firing USER_SIGNED_UP. Wrapped in try/catch because storage reads can throw
// in Safari Lockdown Mode and certain WebView contexts.
export const consumeSignupCountry = (): string => {
  try {
    const raw = localStorage.getItem(SIGNUP_COUNTRY_KEY)
    localStorage.removeItem(SIGNUP_COUNTRY_KEY)
    if (!raw) return ''
    const entry = JSON.parse(raw) as SignupCountryEntry
    if (!entry?.country || !entry?.expiresAt || entry.expiresAt < Date.now()) return ''
    return entry.country
  } catch {
    return ''
  }
}
